import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import NoAxisWarning from '../../NoAxisWarning';
import { getHorsepower } from '@/api/public';
import RadioList, { OptionType } from '@/components/Common/Input/Radio/RadioList';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import { ChevronDownIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { MIN_LENGTH_ERROR_MESSAGE } from '@/const/errorMessage';
import { ADDRESS1, ADDRESS2, ADDRESS3 } from '@/const/products';
import { positiveFloatRegex } from '@/const/regex';
import { useProductEnumContext } from '@/contexts/Products/ProductEnumContext';
import { getSelectableLoadedList, isLengthOnlyLoadedType, shouldShowOnlyLength } from '@/utils/Products/productUtils';
import {
  formatDistanceToThousandKm,
  formatNumber,
  formatValueToDecimalPlaces,
  isUnderFourTons,
  updateSearchParams,
} from '@/utils/common';
import {
  getTonnageErrorMessage,
  processDistanceInput,
  processPowerInput,
  processTonnageInput,
  validateDistance,
  validateLoadedInnerLength,
  validatePower,
} from '@/utils/validation';

interface ProductProps {
  productInfo: ProductDetailResponse;
  setProductInfo: React.Dispatch<React.SetStateAction<ProductDetailResponse>>;
  updateDisabledBtn: (value: boolean) => void;
  isEtc: boolean;
}

const CarInfo: React.FC<ProductProps> = ({ productInfo, setProductInfo, updateDisabledBtn, isEtc }) => {
  const hasPowerValue = (value: string | number | null | undefined) =>
    value !== '' && value !== null && value !== undefined;

  const powerMatchesHorsepowerList = (saved: string | number | null | undefined, horsepower: unknown[]) => {
    if (!hasPowerValue(saved)) return false;
    const n = Number(saved);
    const s = String(saved).trim();
    if (s === '' || Number.isNaN(n)) return false;
    return horsepower.some((hp) => {
      const hs = String(hp).trim();
      return hs === s || Number(hp) === n;
    });
  };

  const powerSessionStorageKey = `carInfoPowerValidationKey:${productInfo?.id ?? 'new'}`;

  const keyValueList: KeyValueListType = {
    manufacturerCategories: '제조사',
    model: '모델',
    modelDetail: '세부 모델',
    color: '색상',
    loaded: '적재함 종류',
    loadedDetail: '세부 적재함 종류',
    transmission: '변속기',
    distance: '주행거리',
    loadedInnerLength: '적재함 길이',
    loadedInnerArea: '적재함 너비',
    loadedInnerHeight: '적재함 높이',
    fuel: '연료',
    power: '마력수',
    garage: '차고지',
    area1: '활동지',
    area2: '지역',
    area3: '지역',
  };
  const [optionData, setOptionData] = useState<OptionType[]>([]);
  const { productEnum, setProductEnum } = useProductEnumContext();
  const [isShow, setIsShow] = useState(false);
  const [title, setTitle] = useState('');
  const [disabledModel, setDisabledModel] = useState<boolean>(false);
  const [tonsError, setTonsError] = useState<boolean>(false);
  const [tonsErrorMsg, setTonsErrorMsg] = useState('');
  const [modelDetailError, setModelDetailError] = useState(false);
  const [modelDetailErrorMsg, setModelDetailErrorMsg] = useState('');
  const [loadedInnerLengthError, setLoadedInnerLengthError] = useState<boolean>(false);
  const [loadedInnerLengthErrorMsg, setLoadedInnerLengthErrorMsg] = useState('');
  const [loadedInnerAreaError, setLoadedInnerAreaError] = useState<boolean>(false);
  const [loadedInnerAreaErrorMsg, setLoadedInnerAreaErrorMsg] = useState('');
  const [loadedInnerHeightError, setLoadedInnerHeightError] = useState<boolean>(false);
  const [loadedInnerHeightErrorMsg, setLoadedInnerHeightErrorMsg] = useState('');
  const [distanceError, setDistanceError] = useState<boolean>(false);
  const [distanceErrorMsg, setDistanceErrorMsg] = useState('');
  const [powerError, setPowerError] = useState(false);
  const [powerErrorMsg, setPowerErrorMsg] = useState('');
  const [horsepowerOptions, setHorsepowerOptions] = useState<OptionType[]>([]);
  const [isLoadingHorsepower, setIsLoadingHorsepower] = useState(false);
  const [isDirectInputMode, setIsDirectInputMode] = useState(false); // 마력수 직접 입력 모드
  const hasValidatedPower = useRef(false);
  const lastValidatedKey = useRef<string>('');

  const loadedLengthInputRef = useRef<HTMLInputElement>(null);
  const loadedAreaInputRef = useRef<HTMLInputElement>(null);
  const loadedHeightInputRef = useRef<HTMLInputElement>(null);
  const distanceRef = useRef<HTMLInputElement>(null);
  const fuelRef = useRef<HTMLInputElement>(null);
  const powerRef = useRef<HTMLInputElement>(null);
  const colorRef = useRef<HTMLInputElement>(null);
  const garageRef = useRef<HTMLInputElement>(null);

  const area1ListRef = useRef<HTMLInputElement>(null);
  const area2ListRef = useRef<HTMLInputElement>(null);
  const area3ListRef = useRef<HTMLInputElement>(null);

  const area1List = ADDRESS1;
  const area1Desc = productInfo?.area1 ?? '';
  const area2List = ADDRESS2[area1Desc] || [];
  const area3List = ADDRESS3[area1Desc] || [];

  const tonsNum = Number(productInfo?.tons);
  const isOneTonRange = tonsNum === 1 || tonsNum === 1.1 || tonsNum === 1.2;

  const getFilterManufacturerCategories = () => {
    if (productInfo?.manufacturerCategories && productEnum) {
      const manufacturerCategories = productEnum['manufacturerAndModel'].map((item) => item.manufacturerCategories);
      return manufacturerCategories;
    }
    return [];
  };

  const getFilteredModels = () => {
    if (productInfo?.manufacturerCategories && productEnum) {
      const models = productEnum['manufacturerAndModel']
        .filter((item) => item.manufacturerCategories.id === productInfo.manufacturerCategories?.id)
        .map((item) => item.model);
      return models;
    }
    return [];
  };

  const getSelectedModel = (): Model | null => {
    if (!productInfo?.manufacturerCategories || !productEnum || !productInfo?.model?.id) return null;
    const flattenedModels = productEnum['manufacturerAndModel']
      .filter((item) => item.manufacturerCategories.id === Number(productInfo.manufacturerCategories?.id))
      .reduce<Model[]>((acc, val) => acc.concat(val.model), []);
    return flattenedModels.find((m) => m.id === Number(productInfo.model?.id)) || null;
  };

  const selectedModelDetailList = getSelectedModel()?.modelDetail ?? [];
  const hasModelDetail = selectedModelDetailList.length > 0;

  useEffect(() => {
    const isMissingRequiredModelDetail = hasModelDetail && !productInfo?.modelDetail?.code;
    if (isMissingRequiredModelDetail) {
      setModelDetailError(true);
      setModelDetailErrorMsg('세부 모델은 필수값입니다.');
    } else {
      setModelDetailError(false);
      setModelDetailErrorMsg('');
    }
  }, [hasModelDetail, productInfo?.modelDetail?.code]);

  const getSelectedLoadedDetailList = (): EnumPresenter[] => {
    if (!isOneTonRange || !productEnum || !productInfo?.loaded?.code) return [];
    const found = (productEnum.oneTonsLoaded ?? []).find((item) => item.code === productInfo.loaded?.code);
    return found?.loadedDetail ?? [];
  };

  const selectedLoadedDetailList = getSelectedLoadedDetailList();
  const hasLoadedDetail = selectedLoadedDetailList.length > 0;

  const getValidLoadedCodeSet = () => {
    if (!productEnum) return new Set<string>();
    if (isOneTonRange) {
      return new Set((productEnum.oneTonsLoaded ?? []).map((item) => String(item.code)));
    }
    return new Set(getSelectableLoadedList(productEnum.loaded, tonsNum).map((item) => String(item.code)));
  };

  useEffect(() => {
    if (!productInfo?.loaded?.code) return;
    // 톤수를 지우고 다시 입력하는 중에는 선택한 적재함 종류를 유지한다.
    if (!productInfo?.tons) return;
    const validLoadedCodeSet = getValidLoadedCodeSet();
    if (validLoadedCodeSet.size === 0) return;

    if (!validLoadedCodeSet.has(String(productInfo.loaded.code))) {
      setProductInfo((prev) => ({
        ...prev,
        loaded: undefined,
        loadedDetail: undefined,
      }));
    }
  }, [isOneTonRange, tonsNum, productEnum, productInfo?.tons, productInfo?.loaded?.code, setProductInfo]);

  useEffect(() => {
    if (!isOneTonRange) return;
    if (!productEnum) return;
    if (!productInfo?.loaded?.code) return;
    if (!productInfo?.loadedDetail?.code) return;

    const loadedItem = (productEnum.oneTonsLoaded ?? []).find((item) => item.code === productInfo.loaded?.code);
    const validDetailCodeSet = new Set((loadedItem?.loadedDetail ?? []).map((item) => String(item.code)));

    if (validDetailCodeSet.size === 0 || !validDetailCodeSet.has(String(productInfo.loadedDetail.code))) {
      setProductInfo((prev) => ({
        ...prev,
        loadedDetail: undefined,
      }));
    }
  }, [isOneTonRange, productEnum, productInfo?.loaded?.code, productInfo?.loadedDetail?.code, setProductInfo]);

  const onClickSelectBox = (key: string) => {
    if (!productEnum) return;

    setTitle(key);

    if (key === 'model') {
      if (!productInfo?.manufacturerCategories) {
        setOptionData([]);
        setIsShow(true);
        return;
      }
      const filteredModels = getFilteredModels();
      if (filteredModels.length > 0) {
        const flattenedArr = filteredModels.reduce<Model[]>((acc, val) => acc.concat(val), []);
        const option = flattenedArr.map((item, index) => {
          return { code: String(item.id), desc: item.name };
        });
        setOptionData(option);
      } else {
        setOptionData([]);
      }
    } else if (key === 'modelDetail') {
      if (!hasModelDetail) return;
      const option = selectedModelDetailList.map((item) => ({ code: item.code, desc: item.desc }));
      setOptionData(option as OptionType[]);
    } else if (key === 'manufacturerCategories') {
      if (!isEtc) {
        setOptionData([]);
        setIsShow(true);
        return;
      }
      const filteredManufacturerCategories = getFilterManufacturerCategories();

      if (filteredManufacturerCategories.length > 0) {
        const flattenedArr = filteredManufacturerCategories.reduce<Model[]>((acc, val) => acc.concat(val), []);
        const option = flattenedArr.map((item, index) => {
          return { code: String(item.id), desc: item.name };
        });
        setOptionData(option);
      } else {
        setOptionData([]);
      }
    } else if (key === 'area1') {
      setOptionData(ADDRESS1.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else if (key === 'area2') {
      const area2Options = ADDRESS2[area1Desc] || [];
      const area3SelectedDesc = productInfo?.area3 ?? '';
      const filteredOptions = area3SelectedDesc
        ? area2Options.filter((item) => item.desc !== area3SelectedDesc)
        : area2Options;
      setOptionData(filteredOptions.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else if (key === 'area3') {
      const area3Options = ADDRESS3[area1Desc] || [];
      const area2SelectedDesc = productInfo?.area2 ?? '';
      const filteredOptions = area2SelectedDesc
        ? area3Options.filter((item) => item.desc !== area2SelectedDesc)
        : area3Options;
      setOptionData(filteredOptions.map((item) => ({ code: item.desc, desc: item.desc })) as OptionType[]);
    } else if (key === 'loaded') {
      if (isOneTonRange) {
        const list = (productEnum.oneTonsLoaded ?? []).map((item) => ({ code: item.code, desc: item.desc }));
        setOptionData(list as OptionType[]);
      } else {
        setOptionData(getSelectableLoadedList(productEnum.loaded, tonsNum) as OptionType[]);
      }
    } else if (key === 'loadedDetail') {
      if (!hasLoadedDetail) return;
      setOptionData(selectedLoadedDetailList as OptionType[]);
    } else {
      setOptionData(productEnum[key] as OptionType[]);
    }
    setIsShow(true);
  };

  const onClickItem = (item: OptionDataType, key: string) => {
    let value = { id: Number(item.code), name: item.desc };
    if (key === 'manufacturerCategories') {
      setProductInfo({
        ...productInfo,
        manufacturerCategories: value,
        model: {},
        modelDetail: undefined,
      } as unknown as ProductDetailResponse);
    } else if (key === 'model') {
      const value = { id: Number(item.code), name: item.desc };
      setProductInfo({ ...productInfo, [key]: value, modelDetail: undefined } as ProductDetailResponse);
    } else if (key === 'modelDetail') {
      setProductInfo({ ...productInfo, modelDetail: { code: String(item.code), desc: String(item.desc) } });
    } else if (key === 'area1') {
      setProductInfo({
        ...productInfo,
        area1: item.desc,
        area2: '',
        area3: '',
      } as ProductDetailResponse);
    } else if (key === 'area2') {
      setProductInfo({
        ...productInfo,
        area2: item.desc,
        ...(productInfo?.area3 === item.desc && { area3: '' }),
      } as ProductDetailResponse);
    } else if (key === 'area3') {
      setProductInfo({
        ...productInfo,
        area3: item.desc,
        ...(productInfo?.area2 === item.desc && { area2: '' }),
      } as ProductDetailResponse);
    } else if (key === 'loaded') {
      setProductInfo({
        ...productInfo,
        loaded: { code: String(item.code), desc: String(item.desc) },
        loadedDetail: undefined,
      } as ProductDetailResponse);
    } else if (key === 'loadedDetail') {
      setProductInfo({
        ...productInfo,
        loadedDetail: { code: String(item.code), desc: String(item.desc) },
      } as ProductDetailResponse);
    } else {
      setProductInfo({ ...productInfo, [key]: item } as ProductDetailResponse);
    }

    setTimeout(() => {
      setIsShow(false);
    }, 200);
  };

  const handleChangeLoadedInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    const decimalPlaces = 2;
    const formattedValue = formatValueToDecimalPlaces(value, decimalPlaces);

    if (name === 'loadedInnerLength' && productInfo) {
      if (!formattedValue.trim()) {
        setLoadedInnerLengthError(false);
        setLoadedInnerLengthErrorMsg('');
      } else {
        const { hasError, errorMsg } = validateLoadedInnerLength(
          Number(formattedValue),
          Number(productInfo.tons),
          productInfo.manufacturerCategories.code,
          productInfo.model.id,
        );
        setLoadedInnerLengthError(hasError);
        setLoadedInnerLengthErrorMsg(errorMsg);
      }
    } else if (name === 'loadedInnerArea' || name === 'loadedInnerHeight') {
      const isBelowMinimum = formattedValue && Number(formattedValue) < 1;
      setLoadedInnerAreaError(name === 'loadedInnerArea' && (!formattedValue || !!isBelowMinimum));
      setLoadedInnerAreaErrorMsg(name === 'loadedInnerArea' && isBelowMinimum ? MIN_LENGTH_ERROR_MESSAGE : '');
      setLoadedInnerHeightError(name === 'loadedInnerHeight' && (!formattedValue || !!isBelowMinimum));
      setLoadedInnerHeightErrorMsg(name === 'loadedInnerHeight' && isBelowMinimum ? MIN_LENGTH_ERROR_MESSAGE : '');
    }

    if (positiveFloatRegex.test(formattedValue.trim()) || formattedValue.trim() === '') {
      const str = formattedValue.trim() === '' ? null : formattedValue.trim();
      setProductInfo({ ...productInfo, [name]: str } as ProductDetailResponse);
    }
  };

  useEffect(() => {
    const filteredModels = getFilteredModels();
    if (filteredModels.length === 1) {
      setDisabledModel(true);

      const flattenedArr = filteredModels.reduce<Model[]>((acc, val) => acc.concat(val), []);
      const [firstModel] = flattenedArr;

      if (flattenedArr.length === 1) {
        setProductInfo({
          ...productInfo,
          model: { id: firstModel.id, name: firstModel.name },
        } as ProductDetailResponse);
      } else {
        setDisabledModel(false);
      }
    }
  }, [productInfo.manufacturerCategories]);

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
    let { value } = e.target;
    const valueNum = Number(value);
    if (valueNum >= 100) {
      const { updatedParams, error, errorMsg } = updateSearchParams(productInfo, key, valueNum);
      setProductInfo(updatedParams);
      switch (key) {
        case 'loadedInnerLength':
          setLoadedInnerLengthError(error);
          setLoadedInnerLengthErrorMsg(errorMsg);
          break;
        case 'loadedInnerArea':
          setLoadedInnerAreaError(error);
          setLoadedInnerAreaErrorMsg(errorMsg);
          break;
        case 'loadedInnerHeight':
          setLoadedInnerHeightError(error);
          setLoadedInnerHeightErrorMsg(errorMsg);
          break;
      }
    }
  };

  useEffect(() => {
    if (!productInfo?.loadedInnerLength) {
      setLoadedInnerLengthError(false);
      setLoadedInnerLengthErrorMsg('');
      return;
    }
    const { hasError, errorMsg } = validateLoadedInnerLength(
      Number(productInfo.loadedInnerLength),
      Number(productInfo.tons),
      productInfo.manufacturerCategories.code,
      productInfo.model.id,
    );
    setLoadedInnerLengthError(hasError);
    setLoadedInnerLengthErrorMsg(errorMsg);
  }, [productInfo?.loadedInnerLength, productInfo?.tons]);

  // 마력수 값 검증 및 자동 설정 함수
  const validatePowerWithApi = async () => {
    const manufacturer = productInfo?.manufacturerCategories?.name;
    const modelName = productInfo?.model?.name;
    const tons = productInfo?.tons;
    const horsePower = String(productInfo?.power ?? '');

    if (!manufacturer || !modelName || !tons) {
      return;
    }

    const validationKey = `${manufacturer}-${modelName}-${tons}`;

    const lastValidationKey = sessionStorage.getItem(powerSessionStorageKey) || lastValidatedKey.current;

    if (hasValidatedPower.current && lastValidatedKey.current === validationKey) {
      return;
    }

    const isKeyChanged = lastValidationKey && lastValidationKey !== validationKey;

    if (isKeyChanged) {
      hasValidatedPower.current = false;
    }

    hasValidatedPower.current = true;
    lastValidatedKey.current = validationKey;

    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower,
      });

      if (response?.result === 'Y' && response.horsepower.length > 0) {
        const savedPower = productInfo?.power;
        if (powerMatchesHorsepowerList(savedPower, response.horsepower)) {
          setIsDirectInputMode(false);
          setPowerError(false);
          setPowerErrorMsg('');
        } else if (hasPowerValue(savedPower)) {
          // DB에 저장된 마력이 외부 목록과 달라도 값은 유지 (직접입력)
          setIsDirectInputMode(true);
          const tonsNorm = processTonnageInput(productInfo?.tons ?? '0', 1);
          const { isValid, errorMessage } = validatePower(String(savedPower), tonsNorm);
          setPowerError(!isValid);
          setPowerErrorMsg(isValid ? '' : errorMessage);
        } else {
          setIsDirectInputMode(false);
          setPowerError(true);
          setPowerErrorMsg('마력을 선택해주세요.');
        }
      } else {
        setIsDirectInputMode(true);
        if (isKeyChanged) {
          setProductInfo((prev) => ({ ...prev, power: '' } as ProductDetailResponse));
          setPowerError(true);
          setPowerErrorMsg('마력을 입력해주세요.');
        } else if (!hasPowerValue(productInfo?.power)) {
          setPowerError(true);
          setPowerErrorMsg('마력을 입력해주세요.');
        } else {
          setPowerError(false);
          setPowerErrorMsg('');
        }
      }
    } catch (error) {
      console.error('마력수 검증 실패:', error);
      setIsDirectInputMode(true);
      if (isKeyChanged) {
        setProductInfo((prev) => ({ ...prev, power: '' } as ProductDetailResponse));
        setPowerError(true);
        setPowerErrorMsg('마력을 입력해주세요.');
      } else {
        if (!hasPowerValue(productInfo?.power)) {
          setPowerError(true);
          setPowerErrorMsg('마력을 입력해주세요.');
        }
      }
    } finally {
      sessionStorage.setItem(powerSessionStorageKey, validationKey);
    }
  };

  useEffect(() => {
    validatePowerWithApi();
  }, [productInfo?.manufacturerCategories?.name, productInfo?.model?.name, productInfo?.tons]);

  useEffect(() => {
    if (!hasPowerValue(productInfo?.power)) {
      const errorMsg = isDirectInputMode ? '마력을 입력해주세요.' : '마력을 선택해주세요.';
      if (!powerError || powerErrorMsg !== errorMsg) {
        setPowerError(true);
        setPowerErrorMsg(errorMsg);
      }
    } else {
      if (powerError) {
        setPowerError(false);
        setPowerErrorMsg('');
      }
    }
  }, [productInfo?.power, powerError, powerErrorMsg, isDirectInputMode]);

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'loadedInnerLength') {
        loadedLengthInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerArea') {
        loadedAreaInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerHeight') {
        loadedHeightInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'power') {
        powerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  };

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => {
      scrollIntoView();
    };
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  const onChangeRadioInput = (newVal: OptionType, key: string) => {
    setProductInfo({ ...productInfo, [key]: newVal } as ProductDetailResponse);
  };

  const isValidatedLoaded = () => {
    if (!productInfo?.loaded) {
      return true;
    } else if (isLengthOnlyLoadedType(productInfo)) {
      return !productInfo?.loadedInnerLength || loadedInnerLengthError;
    } else {
      return (
        !productInfo?.loadedInnerLength ||
        !productInfo?.loadedInnerArea ||
        !productInfo?.loadedInnerHeight ||
        loadedInnerLengthError ||
        loadedInnerAreaError ||
        loadedInnerHeightError
      );
    }
  };

  useEffect(() => {
    updateDisabledBtn(
      !productInfo?.manufacturerCategories?.name ||
        !productInfo?.model?.name ||
        (hasModelDetail && !productInfo?.modelDetail?.code) ||
        tonsError ||
        !!isValidatedLoaded() ||
        distanceError ||
        powerError,
    );
  }, [
    productInfo?.manufacturerCategories?.name,
    productInfo?.model?.name,
    productInfo?.modelDetail?.code,
    tonsError,
    loadedInnerLengthError,
    loadedInnerAreaError,
    loadedInnerHeightError,
    distanceError,
    powerError,
  ]);

  const handleChangeTonsInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const trimmedValue = processTonnageInput(value, 1);

    // tons가 1이 아니면 area1, area2, area3을 ''로 설정
    const updatedProductInfo: ProductDetailResponse = { ...productInfo, [name]: trimmedValue } as ProductDetailResponse;

    // trimmedValue가 '1'이 아니면 무조건 ''로 설정
    const isTonsOne = trimmedValue === '1' || trimmedValue === '1.1' || trimmedValue === '1.2';
    if (!isTonsOne) {
      updatedProductInfo.area1 = '';
      updatedProductInfo.area2 = '';
      updatedProductInfo.area3 = '';
      updatedProductInfo.loadedDetail = undefined;
    }

    setProductInfo(updatedProductInfo);

    const errorMessage = getTonnageErrorMessage(trimmedValue);

    if (errorMessage) {
      setTonsError(true);
      setTonsErrorMsg(errorMessage);
    } else {
      setTonsError(false);
      setTonsErrorMsg('');
    }
  };

  const handleChangeDistanceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const trimmedValue = processDistanceInput(value);
    const { isValid, errorMessage } = validateDistance(trimmedValue);

    setProductInfo({ ...productInfo, [name]: trimmedValue } as ProductDetailResponse);

    if (!isValid) {
      setDistanceError(true);
      setDistanceErrorMsg(errorMessage);
    } else {
      setDistanceError(false);
      setDistanceErrorMsg('');
    }
  };

  const handleChangePowerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    const trimmedValue = processPowerInput(value);

    const tons = processTonnageInput(productInfo?.tons ?? '0', 1);
    const { isValid, errorMessage } = validatePower(trimmedValue, tons);

    setProductInfo({ ...productInfo, [name]: trimmedValue } as ProductDetailResponse);

    if (!isValid) {
      setPowerError(true);
      setPowerErrorMsg(errorMessage);
    } else {
      setPowerError(false);
      setPowerErrorMsg('');
    }
  };

  const fetchHorsepower = async () => {
    const manufacturer = productInfo?.manufacturerCategories?.name;
    const modelName = productInfo?.model?.name;
    const tons = productInfo?.tons;
    const horsePower = String(productInfo?.power ?? '');

    // 파라미터 체크
    if (!manufacturer || !modelName || !tons) {
      return;
    }

    setIsLoadingHorsepower(true);
    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower,
      });

      if (response?.result === 'Y' && Array.isArray(response.horsepower)) {
        if (response.horsepower.length === 0) {
          setIsDirectInputMode(true);
        } else {
          setIsDirectInputMode(false);
          const options: OptionType[] = response.horsepower.map((hp: number) => ({
            code: String(hp),
            desc: String(hp),
          }));
          setHorsepowerOptions(options);
          setTitle('power');
          setIsShow(true);
        }
      } else {
        setIsDirectInputMode(true);
      }
    } catch (error) {
      console.error('마력수 조회 실패:', error);
      setIsDirectInputMode(true);
    } finally {
      setIsLoadingHorsepower(false);
    }
  };

  const handleClickPowerSelectBox = () => {
    fetchHorsepower();
  };

  const isShowOnlyLength = shouldShowOnlyLength(productInfo);

  const handleBlurTonsInput = () => {
    if (isUnderFourTons(productInfo.tons)) {
      setProductInfo({
        ...productInfo,
        axis: { code: 'NONE', desc: '없음' },
      });
    }
    // 톤수 blur 시 마력수 API 호출 및 검증
    hasValidatedPower.current = false;
    lastValidatedKey.current = '';
    sessionStorage.removeItem(powerSessionStorageKey);
    validatePowerWithApi();
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-[30px]">
        {isEtc && (
          <div className="py-5 px-4 rounded-[10px] bg-gray-1 text-red font-medium text-sm xxs:text-xs leading-[18px]">
            * 기타(쌍용 외) 제조사에서 타 제조사로 변경 시 추후 수정이 불가능하니 유의해주세요.
          </div>
        )}

        <div onClick={() => onClickSelectBox('manufacturerCategories')}>
          <TextInputLabelUp
            name="manufacturerCategories"
            label="제조사"
            placeholder="제조사 선택"
            value={productInfo?.manufacturerCategories?.name}
            type="text"
            disabled={!isEtc || false}
            readOnly
            suffix={isEtc && <ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
            required
          ></TextInputLabelUp>
        </div>

        <div onClick={() => onClickSelectBox('model')}>
          <TextInputLabelUp
            name="model"
            label="모델"
            placeholder="모델 선택"
            value={productInfo?.model?.name || ''}
            type="text"
            readOnly
            required
            suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
          ></TextInputLabelUp>
        </div>

        {hasModelDetail && (
          <div onClick={() => onClickSelectBox('modelDetail')}>
            <TextInputLabelUp
              name="modelDetail"
              label="세부 모델"
              placeholder="세부 모델 선택"
              value={productInfo?.modelDetail?.desc || ''}
              type="text"
              readOnly
              required
              error={modelDetailError}
              errorMsg={modelDetailErrorMsg}
              suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
            />
          </div>
        )}

        <TextInputLabelUp
          name="tons"
          label="톤수"
          placeholder="톤수 입력"
          value={productInfo?.tons || ''}
          onChange={(e) => handleChangeTonsInput(e)}
          onBlur={() => handleBlurTonsInput()}
          error={tonsError}
          errorMsg={tonsErrorMsg}
          suffix="t"
          required
          maxLength={4}
        ></TextInputLabelUp>

        <div onClick={() => onClickSelectBox('loaded')}>
          <TextInputLabelUp
            name="loaded"
            label="적재함 종류"
            placeholder="적재함 종류 선택"
            value={productInfo?.loaded?.desc || ''}
            type="text"
            readOnly
            required
            suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
          ></TextInputLabelUp>
        </div>

        {isOneTonRange && hasLoadedDetail && (
          <div onClick={() => onClickSelectBox('loadedDetail')}>
            <TextInputLabelUp
              name="loadedDetail"
              label="세부 적재함 종류"
              placeholder="세부 적재함 종류 선택"
              value={productInfo?.loadedDetail?.desc || ''}
              type="text"
              readOnly
              suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
            />
          </div>
        )}

        <div>
          <TextInputLabelUp
            ref={loadedLengthInputRef}
            name="loadedInnerLength"
            label="길이"
            placeholder="길이 입력"
            value={productInfo?.loadedInnerLength ? String(productInfo?.loadedInnerLength) : ''}
            onChange={(e) => handleChangeLoadedInput(e)}
            onBlur={(e) => handleInputBlur(e, 'loadedInnerLength')}
            onFocus={(e) => scrollIntoView(e)}
            error={loadedInnerLengthError}
            errorMsg={loadedInnerLengthErrorMsg}
            fontSize={18}
            height={36}
            inputMode="numeric"
            suffix="m"
            maxLength={5}
            required
          ></TextInputLabelUp>
          {!isShowOnlyLength && (
            <>
              <div className="flex items-center justify-center w-full pt-5">
                <TextInputLabelUp
                  ref={loadedAreaInputRef}
                  name="loadedInnerArea"
                  label="너비"
                  placeholder="너비 입력"
                  value={productInfo?.loadedInnerArea ? String(productInfo?.loadedInnerArea) : ''}
                  onChange={(e) => handleChangeLoadedInput(e)}
                  onBlur={(e) => handleInputBlur(e, 'loadedInnerArea')}
                  onFocus={(e) => scrollIntoView(e)}
                  error={loadedInnerAreaError}
                  errorMsg={loadedInnerAreaErrorMsg}
                  fontSize={18}
                  height={36}
                  inputMode="numeric"
                  suffix="m"
                  maxLength={5}
                ></TextInputLabelUp>
              </div>

              <div className="flex items-center justify-center w-full pt-5">
                <TextInputLabelUp
                  ref={loadedHeightInputRef}
                  label="높이"
                  name="loadedInnerHeight"
                  placeholder="높이 입력"
                  value={productInfo?.loadedInnerHeight ? String(productInfo?.loadedInnerHeight) : ''}
                  onChange={(e) => handleChangeLoadedInput(e)}
                  onBlur={(e) => handleInputBlur(e, 'loadedInnerHeight')}
                  onFocus={(e) => scrollIntoView(e)}
                  error={loadedInnerHeightError}
                  errorMsg={loadedInnerHeightErrorMsg}
                  fontSize={18}
                  height={36}
                  inputMode="numeric"
                  suffix="m"
                  maxLength={5}
                ></TextInputLabelUp>
              </div>
            </>
          )}
        </div>

        <div>
          <label className="text-base font-medium text-gray-8">
            가변축
            <span className="font-normal text-red">(필수)</span>
          </label>

          <NoAxisWarning tons={productInfo?.tons} className="my-2" />

          <div className="px-1">
            <RadioList
              name="axis"
              list={productEnum?.axis || []}
              value={productInfo?.axis?.code || ''}
              onChange={(val) => {
                onChangeRadioInput(val, 'axis');
              }}
              isDisabled={isUnderFourTons(productInfo?.tons)}
            ></RadioList>
          </div>
        </div>

        <div>
          <label className="text-base font-medium text-gray-8">
            변속기
            <span className="font-normal text-red">(필수)</span>
          </label>
          <div className="px-1">
            <RadioList
              name="transmission"
              list={(productEnum?.transmission.filter((item) => item.code !== 'BOTH') as OptionType[]) || []}
              value={productInfo?.transmission?.code || ''}
              onChange={(val) => {
                onChangeRadioInput(val, 'transmission');
              }}
              justify="equal"
            ></RadioList>
          </div>
        </div>

        <TextInputLabelUp
          ref={distanceRef}
          name="distance"
          label="주행거리"
          placeholder="주행거리 입력"
          value={formatNumber(productInfo?.distance?.toString(), true) || ''}
          onChange={(e) => handleChangeDistanceInput(e)}
          onFocus={(e) => scrollIntoView(e)}
          inputMode="numeric"
          required
          error={distanceError}
          errorMsg={distanceErrorMsg}
          suffix="km"
          maxLength={8}
        ></TextInputLabelUp>

        <div onClick={() => onClickSelectBox('fuel')}>
          <TextInputLabelUp
            ref={fuelRef}
            name="fuel"
            label="연료"
            placeholder="연료 선택"
            value={productInfo?.fuel?.desc || ''}
            type="text"
            readOnly
            suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
            required
          ></TextInputLabelUp>
        </div>

        <div onClick={!isDirectInputMode ? handleClickPowerSelectBox : undefined}>
          <TextInputLabelUp
            ref={powerRef}
            name="power"
            label="마력수"
            placeholder={isDirectInputMode ? '마력수 입력' : '마력수 선택'}
            value={productInfo?.power || ''}
            type="text"
            inputMode={isDirectInputMode ? 'numeric' : undefined}
            readOnly={!isDirectInputMode}
            onChange={isDirectInputMode ? handleChangePowerInput : undefined}
            onFocus={isDirectInputMode ? (e) => scrollIntoView(e) : undefined}
            suffix={isDirectInputMode ? 'PS' : <ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
            required
            error={powerError}
            errorMsg={powerErrorMsg}
            maxLength={isDirectInputMode ? 4 : undefined}
          />
        </div>

        <div onClick={() => onClickSelectBox('color')}>
          <TextInputLabelUp
            ref={colorRef}
            name="color"
            label="색상"
            placeholder="색상 선택"
            value={productInfo?.color?.desc || ''}
            type="text"
            readOnly
            suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
          ></TextInputLabelUp>
        </div>

        <div onClick={() => onClickSelectBox('garage')}>
          <TextInputLabelUp
            ref={garageRef}
            name="garage"
            label="차고지"
            placeholder="차고지 선택"
            value={productInfo?.garage?.desc || ''}
            type="text"
            readOnly
            suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
          ></TextInputLabelUp>
        </div>

        {(Number(productInfo?.tons) === 1 ||
          Number(productInfo?.tons) === 1.1 ||
          Number(productInfo?.tons) === 1.2) && (
          <>
            <div onClick={() => onClickSelectBox('area1')}>
              <TextInputLabelUp
                ref={area1ListRef}
                name="area1"
                label="활동지"
                placeholder="활동지 선택"
                value={productInfo?.area1 ?? ''}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
              />
            </div>

            {area2List.length > 1 && (
              <>
                <div onClick={() => onClickSelectBox('area2')}>
                  <TextInputLabelOff
                    ref={area2ListRef}
                    name="area2"
                    placeholder="지역 선택"
                    fontSize={18}
                    value={productInfo?.area2 ?? ''}
                    readOnly
                    suffix={<ChevronDownIcon color={colors.gray[8]} />}
                  />
                </div>
                <div onClick={() => onClickSelectBox('area3')}>
                  <TextInputLabelOff
                    ref={area3ListRef}
                    name="area3"
                    placeholder="지역 선택"
                    fontSize={18}
                    value={productInfo?.area3 ?? ''}
                    readOnly
                    suffix={<ChevronDownIcon color={colors.gray[8]} />}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <BottomSheetPopup isOpen={isShow} onClose={() => setIsShow(false)} title={keyValueList[title] || '마력수'}>
        <div className="px-4 pb-8">
          {isLoadingHorsepower ? (
            <div className="py-8 text-center text-gray-6">로딩 중...</div>
          ) : (
            <RadioList
              name={title}
              list={title === 'power' ? horsepowerOptions : optionData}
              value={
                title === 'power'
                  ? productInfo?.power || ''
                  : title === 'area1' || title === 'area2' || title === 'area3'
                  ? productInfo?.[title] ?? ''
                  : (productInfo && (productInfo as any)[title]?.id?.toString()) ||
                    (productInfo && (productInfo as any)[title]?.code) ||
                    ''
              }
              onChange={(val) => {
                if (title === 'power') {
                  const selectedPower = val.code;
                  setProductInfo({ ...productInfo, power: selectedPower } as ProductDetailResponse);

                  // 0을 선택한 경우는 유효성 검사 통과
                  if (selectedPower === '0') {
                    setPowerError(false);
                    setPowerErrorMsg('');
                  } else {
                    const tons = processTonnageInput(productInfo?.tons ?? '0', 1);
                    const { isValid, errorMessage } = validatePower(selectedPower, tons);
                    if (!isValid) {
                      setPowerError(true);
                      setPowerErrorMsg(errorMessage);
                    } else {
                      setPowerError(false);
                      setPowerErrorMsg('');
                    }
                  }
                  setTimeout(() => {
                    setIsShow(false);
                  }, 200);
                } else {
                  onClickItem(val, title);
                }
              }}
              horizontal={false}
            ></RadioList>
          )}
        </div>
      </BottomSheetPopup>
    </div>
  );
};

export default CarInfo;
